"use client";

import { useId, useMemo, useState } from "react";
import { ProvenanceBadge } from "./Primitives.web";
import type { KundaliPlanet } from "./types";

const CHART_SIZE = 300;
const MID = CHART_SIZE / 2;
const HOUSE_POSITIONS = [
  { x: 0, y: 0 },
  { x: MID, y: MID * 0.28 },
  { x: MID * 0.28, y: MID * 0.28 },
  { x: MID * 0.28, y: MID },
  { x: MID * 0.28, y: MID * 1.72 },
  { x: MID, y: MID * 1.72 },
  { x: MID * 1.72, y: MID * 1.72 },
  { x: MID * 1.72, y: MID },
  { x: MID * 1.72, y: MID * 0.28 },
  { x: MID * 1.35, y: MID * 0.65 },
  { x: MID * 1.35, y: MID * 1.35 },
  { x: MID * 0.65, y: MID * 1.35 },
  { x: MID * 0.65, y: MID * 0.65 },
] as const;

const PLANET_ABBREVIATIONS: Record<string, string> = {
  sun: "Su",
  moon: "Mo",
  mars: "Ma",
  mercury: "Bu",
  jupiter: "Ju",
  venus: "Shu",
  saturn: "Sha",
  rahu: "Ra",
  ketu: "Ke",
};

export interface KundaliChartProps {
  planets: readonly KundaliPlanet[];
  title?: string;
  subtitle?: string;
  className?: string;
  onPlanetSelect?: (planet: KundaliPlanet) => void;
}

export function KundaliChart({
  planets,
  title = "Janam Kundali",
  subtitle = "North Indian chart · select a graha for details",
  className = "",
  onPlanetSelect,
}: KundaliChartProps) {
  const titleId = useId();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const planetsByHouse = useMemo(() => groupPlanetsByHouse(planets), [planets]);
  const selected = planets.find((planet) => planet.planet_id === selectedId) ?? null;

  function selectPlanet(planet: KundaliPlanet) {
    setSelectedId(String(planet.planet_id));
    onPlanetSelect?.(planet);
  }

  return (
    <section className={`jyotish-kundali ${className}`} aria-labelledby={titleId}>
      <header className="jyotish-kundali__header">
        <div>
          <h3 id={titleId}>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <ProvenanceBadge kind="engine" />
      </header>
      <div className="jyotish-kundali__canvas">
        <svg
          viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
          role="img"
          aria-label="North Indian kundali showing graha positions across 12 houses"
        >
          <ChartFrame />
          {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
            <House
              key={house}
              house={house}
              planets={planetsByHouse[house]}
              selectedId={selectedId}
            />
          ))}
        </svg>
      </div>
      <div className="jyotish-kundali__graha-list" aria-label="Select a graha">
        {planets.map((planet) => {
          const planetId = String(planet.planet_id);
          return (
            <button
              key={planetId}
              type="button"
              aria-pressed={selectedId === planetId}
              onClick={() => selectPlanet(planet)}
            >
              {planetId}
              <span>H{planet.house ?? "—"}</span>
            </button>
          );
        })}
      </div>
      <PlanetDetail planet={selected} />
    </section>
  );
}

function ChartFrame() {
  return (
    <g className="jyotish-kundali__frame" aria-hidden>
      <rect x="1" y="1" width="298" height="298" rx="3" />
      <polygon points={`${MID},0 0,${MID} ${MID},${CHART_SIZE} ${CHART_SIZE},${MID}`} />
      <line x1={MID} y1="0" x2={MID} y2={CHART_SIZE} />
      <line x1="0" y1={MID} x2={CHART_SIZE} y2={MID} />
    </g>
  );
}

function House({
  house,
  planets,
  selectedId,
}: {
  house: number;
  planets: KundaliPlanet[];
  selectedId: string | null;
}) {
  const position = HOUSE_POSITIONS[house];
  return (
    <g>
      <text
        x={position.x}
        y={position.y - (planets.length > 0 ? planets.length * 5 + 4 : 0)}
        className={house === 1 ? "jyotish-kundali__asc" : "jyotish-kundali__house-number"}
      >
        {house === 1 ? "Asc" : house}
      </text>
      {planets.map((planet, index) => (
        <Planet
          key={String(planet.planet_id)}
          planet={planet}
          x={position.x}
          y={position.y + (index - (planets.length - 1) / 2) * 13}
          selected={selectedId === String(planet.planet_id)}
        />
      ))}
    </g>
  );
}

function Planet({
  planet,
  x,
  y,
  selected,
}: {
  planet: KundaliPlanet;
  x: number;
  y: number;
  selected: boolean;
}) {
  const id = String(planet.planet_id).toLowerCase();
  const label = PLANET_ABBREVIATIONS[id] ?? id.slice(0, 2).toUpperCase();
  return (
    <g
      className={selected ? "jyotish-kundali__planet is-selected" : "jyotish-kundali__planet"}
      aria-hidden
    >
      <circle cx={x} cy={y - 3} r="10" />
      <text x={x} y={y}>
        {label}
      </text>
    </g>
  );
}

function PlanetDetail({ planet }: { planet: KundaliPlanet | null }) {
  if (!planet) {
    return (
      <p className="jyotish-kundali__hint">Tap or focus a graha to read its chart position.</p>
    );
  }
  const statuses = [
    planet.is_retrograde ? "Retrograde" : null,
    planet.is_combust ? "Combust" : null,
    planet.is_exalted ? "Exalted" : null,
    planet.is_debilitated ? "Debilitated" : null,
  ].filter(Boolean);
  return (
    <div className="jyotish-kundali__detail" role="status" aria-live="polite">
      <div>
        <strong>{String(planet.planet_id)}</strong>
        <span>
          House {planet.house ?? "—"}
          {planet.rashi ? ` · ${planet.rashi}` : ""}
          {planet.nakshatra ? ` · ${planet.nakshatra}` : ""}
        </span>
      </div>
      <span>{statuses.join(" · ") || "Standard dignity"}</span>
    </div>
  );
}

function groupPlanetsByHouse(planets: readonly KundaliPlanet[]): Record<number, KundaliPlanet[]> {
  const grouped = Object.fromEntries(
    Array.from({ length: 12 }, (_, index) => [index + 1, [] as KundaliPlanet[]])
  ) as Record<number, KundaliPlanet[]>;
  for (const planet of planets) {
    if (planet.house && planet.house >= 1 && planet.house <= 12) grouped[planet.house].push(planet);
  }
  return grouped;
}

export default KundaliChart;
